import { useState } from "react";
import { cn } from "@/shared/lib/utils";

/*
 * Simulated email thread — an attorney reviewing and drafting
 * replies on the same Outlook conversation over a morning.
 */
const THREAD = [
	{ id: 1, action: "review" as const, label: "Read email from J. Smith", time: "9:02 AM", mins: 3 },
	{ id: 2, action: "draft" as const, label: "Draft reply to J. Smith", time: "9:08 AM", mins: 5 },
	{ id: 3, action: "review" as const, label: "Read follow-up from J. Smith", time: "10:15 AM", mins: 2 },
	{ id: 4, action: "draft" as const, label: "Draft second reply", time: "10:20 AM", mins: 4 },
	{ id: 5, action: "review" as const, label: "Read attachment from J. Smith", time: "2:30 PM", mins: 6 },
];

type Mode = "combined" | "grouped" | "individual_draft" | "individual_review" | "individual";

interface EntryGroup {
	label: string;
	ids: number[];
}

function computeGroups(mode: Mode): EntryGroup[] {
	const reviews = THREAD.filter((e) => e.action === "review");
	const drafts = THREAD.filter((e) => e.action === "draft");

	switch (mode) {
		case "combined":
			return [
				{ label: "Smith v. Jones — Email", ids: THREAD.map((e) => e.id) },
			];
		case "grouped":
			return [
				{ label: "Review correspondence from J. Smith", ids: reviews.map((e) => e.id) },
				{ label: "Draft correspondence to J. Smith", ids: drafts.map((e) => e.id) },
			];
		case "individual_draft":
			return [
				{ label: "Review correspondence from J. Smith", ids: reviews.map((e) => e.id) },
				...drafts.map((e) => ({ label: e.label, ids: [e.id] })),
			];
		case "individual_review":
			return [
				...reviews.map((e) => ({ label: e.label, ids: [e.id] })),
				{ label: "Draft correspondence to J. Smith", ids: drafts.map((e) => e.id) },
			];
		case "individual":
			return THREAD.map((e) => ({ label: e.label, ids: [e.id] }));
	}
}

function resolveMode(groupDrafts: boolean, groupReviews: boolean, combine: boolean): Mode {
	if (combine) return "combined";
	if (groupDrafts && groupReviews) return "grouped";
	if (!groupDrafts && groupReviews) return "individual_draft";
	if (groupDrafts && !groupReviews) return "individual_review";
	return "individual";
}

const GROUP_COLORS = [
	{ bg: "bg-blue-500/10", border: "border-blue-500/30", dot: "bg-blue-500", text: "text-blue-600 dark:text-blue-400" },
	{ bg: "bg-amber-500/10", border: "border-amber-500/30", dot: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
	{ bg: "bg-emerald-500/10", border: "border-emerald-500/30", dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
	{ bg: "bg-purple-500/10", border: "border-purple-500/30", dot: "bg-purple-500", text: "text-purple-600 dark:text-purple-400" },
	{ bg: "bg-rose-500/10", border: "border-rose-500/30", dot: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" },
];

/* ─── Toggle switch (self-contained, no external deps) ─── */

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			onClick={() => !disabled && onChange(!checked)}
			className={cn(
				"relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors",
				checked ? "bg-primary" : "bg-muted-foreground/25",
				disabled && "opacity-40 cursor-not-allowed",
				!disabled && "cursor-pointer",
			)}
		>
			<span
				className={cn(
					"pointer-events-none block h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
					checked ? "translate-x-4" : "translate-x-0",
				)}
			/>
		</button>
	);
}

export function EmailGroupingDemo() {
	const [mode, setMode] = useState<Mode>("grouped");
	const groups = computeGroups(mode);

	const combineAll = mode === "combined";
	const groupDrafts = mode === "combined" || mode === "grouped" || mode === "individual_review";
	const groupReviews = mode === "combined" || mode === "grouped" || mode === "individual_draft";

	// Map each thread event → its group index for coloring
	const eventToGroup = new Map<number, number>();
	groups.forEach((g, gi) => {
		for (const id of g.ids) eventToGroup.set(id, gi);
	});

	return (
		<div className="not-prose flex flex-col gap-4 rounded-lg border bg-muted/30 p-4 sm:p-5">
			{/* ─── Toggles (mirrors settings page) ─── */}
			<div className="flex flex-col rounded-md border bg-background divide-y divide-border/40">
				<div className="flex items-center justify-between px-3 py-3">
					<div className="flex flex-col gap-0.5">
						<span className="text-sm font-medium">One entry per thread</span>
						<span className="text-[11px] text-muted-foreground">Merge all drafts and reviews into a single entry</span>
					</div>
					<Toggle
						checked={combineAll}
						onChange={(v) => setMode(resolveMode(groupDrafts, groupReviews, v))}
					/>
				</div>
				{!combineAll && (
					<>
						<div className="flex items-center justify-between px-3 py-3">
							<div className="flex flex-col gap-0.5">
								<span className="text-sm font-medium">Group drafts</span>
								<span className="text-[11px] text-muted-foreground">All drafts in a thread become one entry</span>
							</div>
							<Toggle
								checked={groupDrafts}
								onChange={(v) => setMode(resolveMode(v, groupReviews, false))}
							/>
						</div>
						<div className="flex items-center justify-between px-3 py-3">
							<div className="flex flex-col gap-0.5">
								<span className="text-sm font-medium">Group reviews</span>
								<span className="text-[11px] text-muted-foreground">All reviews in a thread become one entry</span>
							</div>
							<Toggle
								checked={groupReviews}
								onChange={(v) => setMode(resolveMode(groupDrafts, v, false))}
							/>
						</div>
					</>
				)}
			</div>

			<div className="flex flex-col lg:flex-row gap-4">
				{/* ─── Thread table ─── */}
				<div className="flex-1 rounded-md border bg-background overflow-hidden">
					{/* Subject bar */}
					<div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border-b">
						<svg className="h-3.5 w-3.5 text-muted-foreground shrink-0" viewBox="0 0 16 16" fill="none">
							<rect x="1" y="3" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
							<path d="M1 5L8 9.5L15 5" stroke="currentColor" strokeWidth="1.2" />
						</svg>
						<span className="text-xs font-medium text-muted-foreground truncate">
							Re: Smith v. Jones — Discovery Documents
						</span>
					</div>

					{/* Table header */}
					<div className="grid grid-cols-[auto_1fr_60px_44px] gap-x-2 items-center px-3 py-1.5 border-b bg-muted/30 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						<span className="w-12">Type</span>
						<span>Activity</span>
						<span className="text-right">Time</span>
						<span className="text-right">Dur.</span>
					</div>

					{/* Table rows */}
					{THREAD.map((event) => {
						const gi = eventToGroup.get(event.id) ?? 0;
						const color = GROUP_COLORS[gi % GROUP_COLORS.length];
						return (
							<div
								key={event.id}
								className={cn(
									"grid grid-cols-[auto_1fr_60px_44px] gap-x-2 items-center px-3 py-2 border-b last:border-b-0 transition-colors",
									color.bg,
								)}
							>
								<span
									className={cn(
										"w-12 inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
										event.action === "draft"
											? "bg-amber-500/20 text-amber-700 dark:text-amber-400"
											: "bg-blue-500/20 text-blue-700 dark:text-blue-400",
									)}
								>
									{event.action}
								</span>
								<span className="text-sm truncate">{event.label}</span>
								<span className="text-[11px] text-muted-foreground tabular-nums text-right">{event.time}</span>
								<span className="text-[11px] font-medium tabular-nums text-right">{event.mins}m</span>
							</div>
						);
					})}
				</div>

				{/* ─── Arrow ─── */}
				<div className="hidden lg:flex items-center justify-center">
					<svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 20 20" fill="none">
						<path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</div>
				<div className="flex lg:hidden items-center justify-center">
					<svg className="h-5 w-5 text-muted-foreground" viewBox="0 0 20 20" fill="none">
						<path d="M10 4V16M10 16L5 11M10 16L15 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</div>

				{/* ─── Resulting entries table ─── */}
				<div className="flex-1 rounded-md border bg-background overflow-hidden">
					{/* Header */}
					<div className="grid grid-cols-[1fr_56px_44px] gap-x-2 items-center px-3 py-1.5 border-b bg-muted/30 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						<span>Time entry created</span>
						<span className="text-right">Events</span>
						<span className="text-right">Total</span>
					</div>

					{/* Entry rows */}
					{groups.map((group, gi) => {
						const color = GROUP_COLORS[gi % GROUP_COLORS.length];
						const totalMins = group.ids.reduce(
							(sum, id) => sum + (THREAD.find((e) => e.id === id)?.mins ?? 0),
							0,
						);
						return (
							<div
								key={gi}
								className={cn(
									"grid grid-cols-[1fr_56px_44px] gap-x-2 items-center px-3 py-2.5 border-b last:border-b-0 transition-colors",
									color.bg,
								)}
							>
								<div className="flex items-center gap-2 min-w-0">
									<div className={cn("h-2 w-2 rounded-full shrink-0", color.dot)} />
									<span className={cn("text-sm truncate", color.text)}>{group.label}</span>
								</div>
								<span className="text-[11px] text-muted-foreground tabular-nums text-right">
									{group.ids.length}
								</span>
								<span className="text-[11px] font-semibold tabular-nums text-right">
									{totalMins}m
								</span>
							</div>
						);
					})}
				</div>
			</div>

			{/* Footer */}
			<p className="text-center text-[11px] text-muted-foreground">
				Toggle the settings above to see how the same email thread produces different time entries. Change yours in{" "}
				<span className="font-medium text-foreground">Settings &gt; Emails</span>.
			</p>
		</div>
	);
}
